import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { API_BASE_URL } from '../config';
import { getBearerAuthHeaders } from '../utils/authFetchHeaders';
import { useAuth } from '../contexts/AuthContext';
import './BrandOnboarding.css';

/**
 * /brand-onboarding?invite=… — a brand owner sets up their brand on RiskLo.
 *
 * The invite link is single-use (it locks to the first account that opens it)
 * and expires. Whop secrets typed here are encrypted by the server on arrival
 * and never come back; the page only ever learns "set, ending …1234".
 */

export const BRAND_INVITE_STORAGE_KEY = 'risklo_brand_invite';
/** Set by "Sign in to continue" so App returns here once after sign-in. */
export const BRAND_INVITE_RETURN_KEY = 'risklo_brand_invite_return';
const LOGO_PX = 512;
const MAX_LOGO_BYTES = 512 * 1024;

const STEPS = [
  { id: 'brand', title: 'Your brand' },
  { id: 'logo', title: 'Logo & color' },
  { id: 'whop', title: 'Connect Whop' },
  { id: 'team', title: 'Team' },
  { id: 'review', title: 'Review & submit' },
];

function readInviteToken() {
  try {
    const fromUrl = new URLSearchParams(window.location.search).get('invite');
    if (fromUrl) {
      sessionStorage.setItem(BRAND_INVITE_STORAGE_KEY, fromUrl);
      return fromUrl;
    }
    return sessionStorage.getItem(BRAND_INVITE_STORAGE_KEY) || '';
  } catch {
    return '';
  }
}

function forgetInviteToken() {
  try { sessionStorage.removeItem(BRAND_INVITE_STORAGE_KEY); } catch { /* ignore */ }
}

/**
 * Fit any image into a transparent 512×512 PNG (no cropping), so the logo is
 * always square and the right size whatever they upload.
 */
function fitLogo(file) {
  return new Promise((resolve, reject) => {
    if (!file) return reject(new Error('No file selected.'));
    if (!/^image\/(png|jpeg|webp|svg\+xml)$/.test(file.type)) {
      return reject(new Error('Use a PNG, JPG, WebP or SVG image.'));
    }
    if (file.size > 8 * 1024 * 1024) return reject(new Error('That image is over 8 MB.'));
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      const w = img.naturalWidth || LOGO_PX;
      const h = img.naturalHeight || LOGO_PX;
      if (Math.max(w, h) < 256 && file.type !== 'image/svg+xml') {
        return reject(new Error(`That image is only ${w}×${h}. Use one at least 256 px wide.`));
      }
      let size = LOGO_PX;
      let dataUrl = '';
      // Shrink until it fits the 512 KB limit (rarely needed).
      while (size >= 256) {
        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d');
        const scale = Math.min(size / w, size / h);
        const dw = w * scale;
        const dh = h * scale;
        ctx.drawImage(img, (size - dw) / 2, (size - dh) / 2, dw, dh);
        dataUrl = canvas.toDataURL('image/png');
        if (Math.ceil(((dataUrl.length - 22) * 3) / 4) <= MAX_LOGO_BYTES) return resolve(dataUrl);
        size -= 128;
      }
      return reject(new Error('That image is too detailed to fit 512 KB. Try a simpler version.'));
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Could not read that image.'));
    };
    img.src = url;
  });
}

function CopyField({ label, value }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard blocked: the value is still selectable */ }
  };
  return (
    <div className="bo-copy">
      {label ? <span className="bo-copy-label">{label}</span> : null}
      <div className="bo-copy-row">
        <code>{value}</code>
        <button type="button" className="bo-btn bo-btn-small" onClick={copy}>{copied ? 'Copied' : 'Copy'}</button>
      </div>
    </div>
  );
}

function Field({ label, hint, children, wide }) {
  return (
    <label className={`bo-field${wide ? ' bo-field--wide' : ''}`}>
      <span className="bo-field-label">{label}</span>
      {children}
      {hint ? <small className="bo-field-hint">{hint}</small> : null}
    </label>
  );
}

function SecretField({ label, hint, status, value, onChange, placeholder }) {
  return (
    <Field label={label} hint={hint} wide>
      {status?.set ? (
        <span className="bo-secret-set">Saved securely{status.last4 ? `, ending …${status.last4}` : ''}. Type a new one to replace it.</span>
      ) : null}
      <input
        type="password"
        autoComplete="off"
        spellCheck={false}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
    </Field>
  );
}

export default function BrandOnboarding({ onNavigate }) {
  const { user, loading: authLoading } = useAuth();
  const token = useMemo(readInviteToken, []);
  const [invite, setInvite] = useState(null);
  const [form, setForm] = useState({});
  const [secrets, setSecrets] = useState({ whopApiKey: '', whopWebhookSecret: '' });
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [whopTest, setWhopTest] = useState(null);
  const [testing, setTesting] = useState(false);

  const webhookUrl = `${API_BASE_URL}/api/webhooks/whop`;
  const url = `${API_BASE_URL}/api/brand-onboarding/invite/${encodeURIComponent(token)}`;

  const applyInvite = useCallback((next) => {
    setInvite(next);
    const s = next.submission || {};
    setForm({
      brandName: s.brandName || '',
      resultsLabel: s.resultsLabel || '',
      website: s.website || '',
      accentColor: s.accentColor || '#22c55e',
      checkoutUrl: s.checkoutUrl || '',
      whopCompanyId: s.whopCompanyId || '',
      ownerName: s.ownerName || user?.name || '',
      ownerEmail: s.ownerEmail || user?.email || '',
      staffEmails: (s.staffEmails || []).join(', '),
      tradeWatchSupportEmails: (s.tradeWatchSupportEmails || []).join(', '),
      embedDomains: (s.embedDomains || []).join(', '),
      notes: s.notes || '',
      logoDataUrl: s.logoDataUrl || '',
    });
  }, [user?.name, user?.email]);

  useEffect(() => {
    if (authLoading || !user?.email || !token) {
      setLoading(false);
      return;
    }
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(url, { headers: getBearerAuthHeaders() });
        const data = await res.json();
        if (!res.ok || !data.success) throw new Error(data.error || 'Could not open this invite.');
        applyInvite(data.invite);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [authLoading, user?.email, token, url, applyInvite]);

  const set = (key) => (value) => setForm((prev) => ({ ...prev, [key]: value }));

  const save = async ({ submit = false } = {}) => {
    setSaving(true);
    setError('');
    setNotice('');
    try {
      const typedSecrets = Object.fromEntries(Object.entries(secrets).filter(([, v]) => v.trim()));
      const res = await fetch(url, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getBearerAuthHeaders() },
        body: JSON.stringify({ submission: form, secrets: typedSecrets, submit }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Could not save.');
      applyInvite(data.invite);
      setSecrets({ whopApiKey: '', whopWebhookSecret: '' });
      setNotice(submit ? '' : 'Saved.');
      return true;
    } catch (e) {
      setError(e.message);
      return false;
    } finally {
      setSaving(false);
    }
  };

  const go = async (next) => {
    if (invite?.editable && !(await save())) return;
    setNotice('');
    setStep(next);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const onLogo = async (file) => {
    setError('');
    try {
      set('logoDataUrl')(await fitLogo(file));
    } catch (e) {
      setError(e.message);
    }
  };

  const testWhop = async () => {
    setTesting(true);
    setWhopTest(null);
    try {
      const res = await fetch(`${url}/test-whop`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getBearerAuthHeaders() },
        body: JSON.stringify({ whopCompanyId: form.whopCompanyId, whopApiKey: secrets.whopApiKey }),
      });
      const data = await res.json();
      setWhopTest(res.ok ? data : { ok: false, message: data.error || 'Test failed.' });
    } catch {
      setWhopTest({ ok: false, message: 'Could not run the test.' });
    } finally {
      setTesting(false);
    }
  };

  const signIn = () => {
    // The token is already in sessionStorage; App sends them back here after sign-in.
    try { sessionStorage.setItem(BRAND_INVITE_RETURN_KEY, '1'); } catch { /* ignore */ }
    if (onNavigate) onNavigate('login');
  };

  if (!token) {
    return (
      <div className="bo-page">
        <div className="bo-panel bo-center">
          <h1>Brand setup</h1>
          <p>This page needs an invite link from the RiskLo team. Ask them to send you one.</p>
        </div>
      </div>
    );
  }

  if (!authLoading && !user?.email) {
    return (
      <div className="bo-page">
        <div className="bo-panel bo-center">
          <h1>Set up your brand on RiskLo</h1>
          <p>Sign in first. This invite will be linked to the account you sign in with, so use the one you&apos;ll manage the brand from.</p>
          <button type="button" className="bo-btn bo-btn-primary" onClick={signIn}>Sign in to continue</button>
        </div>
      </div>
    );
  }

  if (loading || authLoading) {
    return <div className="bo-page"><p className="bo-muted">Loading…</p></div>;
  }

  if (!invite) {
    return (
      <div className="bo-page">
        <div className="bo-panel bo-center">
          <h1>Brand setup</h1>
          <p className="bo-error" role="alert">{error || 'This invite could not be opened.'}</p>
        </div>
      </div>
    );
  }

  if (invite.status === 'submitted' || invite.status === 'approved') {
    if (invite.status === 'approved') forgetInviteToken();
    return (
      <div className="bo-page">
        <div className="bo-panel bo-center">
          <h1>{invite.status === 'approved' ? 'Your brand is live' : 'Submitted, thank you!'}</h1>
          <p>
            {invite.status === 'approved'
              ? 'Your brand has been approved. Use the brand switcher in RiskLo to open it.'
              : 'The RiskLo team is reviewing your brand. We\'ll reach out if anything needs changing; you can close this page.'}
          </p>
          {invite.status === 'approved' && onNavigate ? (
            <button type="button" className="bo-btn bo-btn-primary" onClick={() => onNavigate('dashboard')}>Open RiskLo</button>
          ) : null}
        </div>
      </div>
    );
  }

  if (!invite.editable) {
    return (
      <div className="bo-page">
        <div className="bo-panel bo-center">
          <h1>Brand setup</h1>
          <p>This invite has expired. Ask the RiskLo team for a new link.</p>
        </div>
      </div>
    );
  }

  const current = STEPS[step].id;
  const s = invite.secrets || {};

  return (
    <div className="bo-page">
      <header className="bo-head">
        <h1>Set up your brand on RiskLo</h1>
        <p className="bo-muted">
          Takes about 10 minutes. Your progress is saved each time you press Next. This link expires
          {' '}{new Date(invite.expiresAt).toLocaleDateString()}.
        </p>
      </header>

      {invite.status === 'changes_requested' && invite.reviewNote ? (
        <div className="bo-callout" role="status">
          <strong>The RiskLo team asked for changes:</strong> {invite.reviewNote}
        </div>
      ) : null}

      <ol className="bo-steps" aria-label="Steps">
        {STEPS.map((st, i) => (
          <li key={st.id} className={i === step ? 'is-current' : i < step ? 'is-done' : ''}>
            <button type="button" onClick={() => go(i)} disabled={saving}>
              <span className="bo-step-num">{i + 1}</span>
              <span className="bo-step-title">{st.title}</span>
            </button>
          </li>
        ))}
      </ol>

      {error ? <div className="bo-error" role="alert">{error}</div> : null}
      {notice ? <div className="bo-notice" role="status">{notice}</div> : null}

      <section className="bo-panel">
        {current === 'brand' ? (
          <div className="bo-grid">
            <Field label="Brand name" hint="How it appears across RiskLo." >
              <input value={form.brandName} maxLength={60} onChange={(e) => set('brandName')(e.target.value)} placeholder="GemAlgo" />
            </Field>
            <Field label="Results page label" hint="Optional. Defaults to “<Brand> Results”.">
              <input value={form.resultsLabel} maxLength={60} onChange={(e) => set('resultsLabel')(e.target.value)} placeholder={`${form.brandName || 'GemAlgo'} Results`} />
            </Field>
            <Field label="Website" hint="Optional.">
              <input value={form.website} onChange={(e) => set('website')(e.target.value)} placeholder="https://gemalgo.com" />
            </Field>
            <Field label="Your name">
              <input value={form.ownerName} maxLength={80} onChange={(e) => set('ownerName')(e.target.value)} />
            </Field>
            <Field label="Your email" hint="The main contact for this brand. They get access when it's approved.">
              <input type="email" value={form.ownerEmail} onChange={(e) => set('ownerEmail')(e.target.value)} />
            </Field>
          </div>
        ) : null}

        {current === 'logo' ? (
          <div className="bo-grid">
            <Field label="Logo" hint="Any PNG, JPG, WebP or SVG at least 256 px wide. A square logo with a transparent background looks best; we fit it into a 512×512 square for you." wide>
              <input type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" onChange={(e) => onLogo(e.target.files?.[0])} />
            </Field>
            {form.logoDataUrl ? (
              <div className="bo-logo-preview bo-field--wide">
                <div className="bo-preview-tile bo-preview-tile--dark">
                  <img src={form.logoDataUrl} alt="" />
                  <span>{form.brandName || 'Your brand'}</span>
                </div>
                <div className="bo-preview-tile bo-preview-tile--light">
                  <img src={form.logoDataUrl} alt="" />
                  <span>{form.brandName || 'Your brand'}</span>
                </div>
                <small className="bo-field-hint">How it looks in the brand switcher, on dark and light themes.</small>
              </div>
            ) : null}
            <Field label="Accent color" hint="Used for highlights in your brand's pages.">
              <div className="bo-color">
                <input type="color" value={form.accentColor || '#22c55e'} onChange={(e) => set('accentColor')(e.target.value)} />
                <input value={form.accentColor} maxLength={7} onChange={(e) => set('accentColor')(e.target.value)} />
              </div>
            </Field>
          </div>
        ) : null}

        {current === 'whop' ? (
          <div className="bo-whop">
            <p className="bo-muted">
              RiskLo uses Whop to know who your paying members are. Do these four things in your Whop
              dashboard. The keys you paste here are encrypted straight away; nobody at RiskLo can read
              them back, and they&apos;re only used to check memberships.
            </p>

            <div className="bo-task">
              <h3><span>1</span> Company ID</h3>
              <p>Open your Whop dashboard. The address bar shows your company ID, the part starting with <code>biz_</code> (for example <code>whop.com/dashboard/biz_AbC123…</code>).</p>
              <Field label="Whop company ID">
                <input value={form.whopCompanyId} onChange={(e) => set('whopCompanyId')(e.target.value.trim())} placeholder="biz_…" />
              </Field>
            </div>

            <div className="bo-task">
              <h3><span>2</span> API key</h3>
              <p>In Whop go to <strong>Developer → API keys → Create API key</strong>. Give it permission to <strong>read members and memberships</strong>, nothing else. Copy the key and paste it here.</p>
              <SecretField
                label="Whop API key"
                status={s.whopApiKey}
                value={secrets.whopApiKey}
                onChange={(v) => setSecrets((p) => ({ ...p, whopApiKey: v }))}
                placeholder={s.whopApiKey?.set ? '••••••••' : 'Paste the API key'}
              />
              <div className="bo-test">
                <button type="button" className="bo-btn" onClick={testWhop} disabled={testing || !form.whopCompanyId}>
                  {testing ? 'Testing…' : 'Test connection'}
                </button>
                {whopTest ? (
                  <span className={whopTest.ok ? 'bo-ok' : 'bo-bad'}>{whopTest.message}</span>
                ) : null}
              </div>
            </div>

            <div className="bo-task">
              <h3><span>3</span> Webhook</h3>
              <p>In Whop go to <strong>Developer → Webhooks → Create webhook</strong>, then:</p>
              <CopyField label="Webhook URL" value={webhookUrl} />
              <p>Turn on these events:</p>
              <CopyField value="membership.activated" />
              <CopyField value="membership.deactivated" />
              <p>Save it, then copy the <strong>signing secret</strong> (it starts with <code>ws_</code>) and paste it here.</p>
              <SecretField
                label="Webhook signing secret"
                status={s.whopWebhookSecret}
                value={secrets.whopWebhookSecret}
                onChange={(v) => setSecrets((p) => ({ ...p, whopWebhookSecret: v }))}
                placeholder={s.whopWebhookSecret?.set ? '••••••••' : 'ws_…'}
              />
            </div>

            <div className="bo-task">
              <h3><span>4</span> Checkout link</h3>
              <p>The Whop checkout link for the product that gives access to your brand on RiskLo. We send people here to join.</p>
              <Field label="Checkout URL">
                <input value={form.checkoutUrl} onChange={(e) => set('checkoutUrl')(e.target.value.trim())} placeholder="https://whop.com/checkout/…" />
              </Field>
            </div>
          </div>
        ) : null}

        {current === 'team' ? (
          <div className="bo-grid">
            <Field label="Team emails" hint="People on your team who should be able to open your brand in RiskLo. Comma-separated." wide>
              <textarea rows={2} value={form.staffEmails} onChange={(e) => set('staffEmails')(e.target.value)} placeholder="partner@brand.com, ops@brand.com" />
            </Field>
            <Field label="Trade alert emails" hint="Optional. Who should get naked-trade alert emails for your traders." wide>
              <textarea rows={2} value={form.tradeWatchSupportEmails} onChange={(e) => set('tradeWatchSupportEmails')(e.target.value)} placeholder="support@brand.com" />
            </Field>
            <Field label="Websites that will embed your RiskLo charts" hint="Optional. Domains only, e.g. brand.com." wide>
              <input value={form.embedDomains} onChange={(e) => set('embedDomains')(e.target.value)} placeholder="brand.com" />
            </Field>
            <Field label="Anything else we should know?" wide>
              <textarea rows={3} maxLength={1000} value={form.notes} onChange={(e) => set('notes')(e.target.value)} />
            </Field>
          </div>
        ) : null}

        {current === 'review' ? (
          <div className="bo-review">
            <dl>
              <dt>Brand</dt><dd>{form.brandName || <em>missing</em>}</dd>
              <dt>Logo</dt><dd>{form.logoDataUrl ? <img src={form.logoDataUrl} alt="" className="bo-review-logo" /> : <em>missing</em>}</dd>
              <dt>Color</dt><dd><span className="bo-swatch" style={{ background: form.accentColor }} /> {form.accentColor}</dd>
              <dt>Owner</dt><dd>{form.ownerName} {form.ownerEmail ? `(${form.ownerEmail})` : <em>email missing</em>}</dd>
              <dt>Whop company</dt><dd>{form.whopCompanyId || <em>missing</em>}</dd>
              <dt>API key</dt><dd>{s.whopApiKey?.set ? `Saved, ending …${s.whopApiKey.last4}` : <em>missing</em>}</dd>
              <dt>Webhook secret</dt><dd>{s.whopWebhookSecret?.set ? `Saved, ending …${s.whopWebhookSecret.last4}` : <em>missing</em>}</dd>
              <dt>Checkout</dt><dd>{form.checkoutUrl || <em>missing</em>}</dd>
              <dt>Team</dt><dd>{form.staffEmails || '—'}</dd>
            </dl>
            <p className="bo-muted">Nothing goes live until the RiskLo team approves it.</p>
          </div>
        ) : null}
      </section>

      <footer className="bo-nav">
        {step > 0 ? (
          <button type="button" className="bo-btn" onClick={() => go(step - 1)} disabled={saving}>Back</button>
        ) : <span />}
        {step < STEPS.length - 1 ? (
          <button type="button" className="bo-btn bo-btn-primary" onClick={() => go(step + 1)} disabled={saving}>
            {saving ? 'Saving…' : 'Next'}
          </button>
        ) : (
          <button type="button" className="bo-btn bo-btn-primary" onClick={() => save({ submit: true })} disabled={saving}>
            {saving ? 'Submitting…' : 'Submit for review'}
          </button>
        )}
      </footer>
    </div>
  );
}
